<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Initiate Exit</h5>
        <p class="page-sub">Create a new employee exit case</p>
      </div>
      <button class="btn-outline" @click="$router.push($url('/hr/exits'))">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <div class="form-card" style="max-width:680px;margin:0 auto;">

      <!-- Employee -->
      <div class="section-label">Employee</div>
      <div class="form-row">
        <label class="form-label">Select Employee <span class="req">*</span></label>
        <search-select
          v-model="form.employee_id"
          url="payroll/employees/active"
          query-param="search"
          label-key="full_name"
          sublabel-key="employee_no"
          value-key="id"
          response-key="data"
          placeholder="— Choose employee —"
          search-placeholder="Type name or number…"
          :min-chars="1"
        />
      </div>

      <hr class="section-divider" />

      <!-- Exit type & dates -->
      <div class="section-label">Exit Details</div>

      <div class="form-row">
        <label class="form-label">Exit Type <span class="req">*</span></label>
        <select v-model="form.type" class="form-input">
          <option value="">— Choose type —</option>
          <option value="resignation">Resignation</option>
          <option value="termination">Termination</option>
          <option value="retirement">Retirement</option>
          <option value="redundancy">Redundancy</option>
          <option value="death">Death in Service</option>
        </select>
      </div>

      <div class="form-grid-2">
        <div class="form-row">
          <label class="form-label">Notice / Exit Date</label>
          <input v-model="form.exit_date" type="date" class="form-input" />
        </div>
        <div class="form-row">
          <label class="form-label">Last Working Day</label>
          <input v-model="form.last_working_day" type="date" class="form-input" />
        </div>
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">Notice Period (days)</label>
        <input v-model.number="form.notice_period_days" type="number" min="0" max="365" class="form-input" style="max-width:160px;" :placeholder="defaultNotice + ' (default)'" />
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">Reason</label>
        <textarea v-model="form.reason" class="form-input" rows="3" placeholder="Optional reason…" style="resize:vertical;"></textarea>
      </div>

      <div class="form-row" style="margin-top:16px;">
        <label class="form-label">Confidential Notes <span class="hint">(HR only)</span></label>
        <textarea v-model="form.confidential_notes" class="form-input" rows="3" style="resize:vertical;" placeholder="Internal notes not visible to the employee…"></textarea>
      </div>

      <hr class="section-divider" />

      <div class="setting-row">
        <div class="toggle-info">
          <span class="toggle-name">Submit for Approval</span>
          <span class="toggle-hint">Route through the exit approval workflow before proceeding</span>
        </div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="form.submit_for_approval" />
          <span class="toggle-track"></span>
        </label>
      </div>

      <div v-if="error" class="alert-error">{{ error }}</div>

      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:24px;">
        <button class="btn-outline" @click="$router.push($url('/hr/exits'))">Cancel</button>
        <button class="btn-primary-action" @click="submit" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Saving…' : 'Create Exit Case' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import SearchSelect from '../../../../components/SearchSelect.vue'

export default {
  name: 'ExitCreate',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },
  data() {
    return {
      saving: false,
      error: '',
      defaultNotice: 30,
      form: {
        employee_id: '',
        type: '',
        exit_date: '',
        last_working_day: '',
        notice_period_days: null,
        reason: '',
        confidential_notes: '',
        submit_for_approval: false,
      },
    }
  },
  created() { this.loadSettings() },
  methods: {
    async loadSettings() {
      try {
        const r = await axios.get('exits/settings')
        this.defaultNotice = r.data.data?.default_notice_days ?? 30
      } catch { /* non-fatal */ }
    },
    async submit() {
      this.error = ''
      if (!this.form.employee_id) { this.error = 'Please select an employee.'; return }
      if (!this.form.type)        { this.error = 'Please select an exit type.'; return }

      this.saving = true
      try {
        const r = await axios.post('exits', {
          ...this.form,
          notice_period_days: this.form.notice_period_days || undefined,
          exit_date:          this.form.exit_date || undefined,
          last_working_day:   this.form.last_working_day || undefined,
          reason:             this.form.reason || undefined,
          confidential_notes: this.form.confidential_notes || undefined,
        })
        this.toast.success('Exit case created.')
        this.$router.push(this.$url('/hr/exits/' + r.data.data.id))
      } catch (e) {
        this.error = e.response?.data?.message ?? 'Failed to create exit case.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1.5px solid #004AAD; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f0f4ff; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.req   { color:#ef4444; }
.hint  { font-weight:400; color:#aab8cc; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }

.form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top:16px; }
@media(max-width:540px) { .form-grid-2 { grid-template-columns:1fr; } }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }

.setting-row { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:4px 0; }
.toggle-info { display:flex; flex-direction:column; gap:3px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint { font-size:12px; color:#7a90b0; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.alert-error { background:#fee2e2; color:#991b1b; border-radius:8px; padding:10px 14px; font-size:13px; margin-top:14px; }
</style>
